import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef } from "react";

interface UseDebounceOptions {
	delay?: number;
	leading?: boolean;
	trailing?: boolean;
}

// biome-ignore lint/suspicious/noExplicitAny:
export function useDebouncedCallback<T extends (...args: any[]) => any>(
	callback: T,
	options: UseDebounceOptions = {},
) {
	const { delay = 300, leading = true, trailing = true } = options;

	// Keep track of the callback in a ref to prevent unnecessary debounce recreations
	const callbackRef = useRef(callback);
	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	// Create a memoized debounced function
	// biome-ignore lint/correctness/useExhaustiveDependencies:
	const debouncedCallback = useCallback(
		debounce(
			(...args: Parameters<T>) => {
				callbackRef.current(...args);
			},
			delay,
			{ leading, trailing },
		),
		[delay, leading, trailing],
	);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			debouncedCallback.cancel();
		};
	}, [debouncedCallback]);

	return debouncedCallback;
}
